import { useCallback, useEffect, useState } from 'react';
import { Maybe } from '@csp/csp-common-model';
import { useLocation, useNavigate } from 'react-router-dom';
import { AuthenticatedRedirectContextState } from '../model/AuthenticatedRedirectContextState';
import { useInitialRedirectToValue } from './useInitialRedirectToValue';

export const useAuthenticatedRedirectContextProvider = (): AuthenticatedRedirectContextState => {
  const navigate = useNavigate();
  const location = useLocation();

  const initialRedirectToValue = useInitialRedirectToValue();
  const [redirectTo, setRedirectTo] = useState<Maybe<string>>(initialRedirectToValue);
  const [isRedirecting, setIsRedirecting] = useState<boolean>(false);

  const clearRedirectTo = useCallback(() => setRedirectTo(undefined), [setRedirectTo]);
  const canRedirect = !!redirectTo && redirectTo !== location.pathname;
  const startRedirect = useCallback(() => {
    if (!canRedirect) {
      return;
    }
    setIsRedirecting(true);
    navigate(redirectTo, { replace: true });
  }, [setIsRedirecting, canRedirect, navigate, redirectTo]);

  const state: AuthenticatedRedirectContextState = {
    redirectTo,
    setRedirectTo,
    clearRedirectTo,
    canRedirect,
    startRedirect,
  };

  // Reset redirect state when redirect has been completed
  useEffect(() => {
    if (isRedirecting && location.pathname === redirectTo) {
      clearRedirectTo();
      setIsRedirecting(false);
    }
  }, [location, clearRedirectTo, redirectTo, isRedirecting]);

  return state;
};
